import moment from 'moment';
import { get, omit } from 'lodash';
import appConfig from 'Config';
import { BE_TRACKING } from 'Tracking/constants';
import * as trackingStorageApi from 'Tracking/tracking-storage-api';
import * as trackingApi from 'Tracking/tracking-api';
import {
  PAGE_BE_SERVICES,
  PAGE_BE_SERVICES_ALL_INCLUSIVE,
} from 'Utils/booking-engine-pages/BookingPagesUtils';
import { apiDateToTimestamp } from 'Utils/date/apiDate';
import { isSummer } from 'Utils/tracking/resort';
import { BE_GA_DEFAULT_PAYLOAD, BE_CATEGORY_GA } from './constants';

function storeTracking(params: any) {
  const resortTrackingProps = trackingStorageApi.get(BE_TRACKING);
  const bookingCriteriaProps = { ...omit(resortTrackingProps, 'children'), ...params };
  trackingStorageApi.set(bookingCriteriaProps);
}

export function trackDelCredereBanking(dataObject: any, appContext: AppContext) {
  trackingApi.setDataLayer(
    {
      ...BE_GA_DEFAULT_PAYLOAD,
      versionBooking: 'responsive',
      actionGA: 'banking',
      agenceType: 'Ducroire',
      ...dataObject,
    },
    appContext,
  );
}
export function trackNavigation(
  actionGA: string,
  labelGA: string,
  appContext: AppContext,
  addAgencyType: boolean = false,
) {
  trackingApi.setDataLayer(
    {
      ...BE_GA_DEFAULT_PAYLOAD,
      versionBooking: 'responsive',
      actionGA,
      labelGA,
      ...(addAgencyType && {
        agenceType: appContext.cmta.isDelCredere ? 'Ducroire' : 'NonDucroire',
      }),
    },
    appContext,
  );
}
export function trackSaveCriteria(actionGA: string, labelGA: string, appContext: AppContext) {
  trackingApi.setDataLayer(
    {
      ...BE_GA_DEFAULT_PAYLOAD,
      versionBooking: 'responsive',
      categoryGA: 'saveMyCriteria',
      actionGA,
      labelGA,
    },
    appContext,
  );
}
export function trackShareContextDesktopPopin(actionGA: string, appContext: AppContext) {
  trackingApi.setDataLayer(
    { ...BE_GA_DEFAULT_PAYLOAD, versionBooking: 'responsive', labelGA: 'shareValidate', actionGA },
    appContext,
  );
}
export function trackShareContext(actionGA: string, appContext: AppContext) {
  trackingApi.setDataLayer(
    { ...BE_GA_DEFAULT_PAYLOAD, versionBooking: 'responsive', labelGA: 'shareClick', actionGA },
    appContext,
  );
}
export function trackShareContextMobilePopin(
  actionGA: string,
  socialNetworkName: string,
  appContext: AppContext,
) {
  trackingApi.setDataLayer(
    {
      ...BE_GA_DEFAULT_PAYLOAD,
      versionBooking: 'responsive',
      labelGA: `shareValidate_${socialNetworkName}`,
      actionGA,
    },
    appContext,
  );
}
type TrackingParams = {
  basePriceLabel: string;
  pageName: string;
  resort: Product;
  userInfo?: UserInfo;
  proposals: Array<Proposal>;
  selectedProposal: Proposal;
  leadScore?: number;
  customEventName?: string;
};
export function trackPage(trackingParams: TrackingParams, appContext: AppContext) {
  const {
    basePriceLabel,
    pageName,
    resort,
    userInfo,
    selectedProposal,
    leadScore,
    customEventName = null,
  } = trackingParams;
  const transportDeparture = get(
    selectedProposal.transports,
    'inbound.travelSections[0].departure',
    {},
  );
  const storedTracking = trackingStorageApi.get(BE_TRACKING);
  const departureDate = get(selectedProposal, 'departureDate', null);
  const departureDateTimestamp = apiDateToTimestamp(departureDate);
  const computedPageName =
    pageName === PAGE_BE_SERVICES && resort.isJoyview ? PAGE_BE_SERVICES_ALL_INCLUSIVE : pageName;
  const trackingDataToStore = {
    resortCode: resort.id,
    resortName: resort.name,
    resortTrident: resort.comfort,
    resortCountry: resort.destination.country,
    resortRegion: resort.destination.area,
    resortAdult: selectedProposal.attendees.adults.length,
    resortChildren: selectedProposal.attendees.children.length,
    resortDefaultPrice: get(resort, 'price.initialPrice.value'),
    resortDepartureCityCode: transportDeparture.cityId || 'WITHOUT TRANSPORT',
    resortDepartureCityName: transportDeparture.label,
    resortDepartureDate: selectedProposal.arrivalDate,
    resortNightNumber: selectedProposal.duration,
    resortOldPrice: get(resort, 'price.initialPrice.value'),
    resortSeasonType: get(resort, 'seasonName'),
    resortTotalParticipant: selectedProposal.attendees.list.length,
    resortTransport: selectedProposal.transports ? 'with Transport' : 'without Transport',
    resortUrlImage: resort.image,
    versionBooking: 'responsive',
    resortSeason:
      departureDateTimestamp && isSummer(new Date(departureDateTimestamp)) ? 'Summer' : 'Winter',
    resortYearSeason: moment(departureDate, 'YYYYMMDD').format('YYYY'),
    [basePriceLabel]: selectedProposal.price.total.value,
    contextNumber: selectedProposal.id,
    currencyCode: selectedProposal.price.currency,
    daysBeforeDeparture: moment(selectedProposal.arrivalDate).diff(moment(), 'days'),
    upsellExcursion: selectedProposal.additionalServices.activities.some(
      ({ pricingInfo }) => pricingInfo.isPartiallyRented,
    )
      ? 'yes'
      : 'no',
    // @ts-expect-error TODO(flow-migration)
    BE_customerStatus: storedTracking.BE_customerStatus,
    ...(leadScore
      ? {
          leadScore,
        }
      : null),
  };
  storeTracking(trackingDataToStore);
  trackingApi.setDataLayer(
    {
      categoryGA: BE_CATEGORY_GA,
      locale: appContext.locale,
      event: customEventName || computedPageName,
      fidelityStatus: userInfo?.loyaltyProgram?.status || '',
      valueGA: 0,
      environment: appConfig.get('tracking.environment'),
      ...(appContext.isCmta && {
        agenceType: appContext.cmta.isDelCredere ? 'Ducroire' : 'NonDucroire',
      }),
      nonInteraction: 0,
      pageName: `BE_${computedPageName}`,
      siteSection: 'Booking Engine',
      ...trackingDataToStore,
    },
    appContext,
  );
}
export const resetTrackingStorageBE = (isTravelPlan: boolean = false) => {
  storeTracking({
    BE_customerStatus: null,
    is_travel_plan: isTravelPlan,
  });
};
